import axios from 'axios';
import {
  Appointment,
  Article,
  Service,
  GalleryItem,
  Contact,
  About,
  Comment,
  Feedback,
  AppointmentFormData,
  ContactFormData,
  CommentFormData,
  FeedbackFormData,
  PaginatedResponse,
  StaticPage,
  CustomSeo
} from '../types';
import { API_BASE_URL } from '../config/runtime';

export { API_BASE_URL } from '../config/runtime';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token to every request if available
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Appointment API
export const appointmentAPI = {
  getAll: () => api.get<Appointment[]>('/appointments'),
  getById: (id: string) => api.get<Appointment>(`/appointments/${id}`),
  create: (data: AppointmentFormData) => api.post<Appointment & { manageToken?: string }>('/appointments', data),
  update: (id: string, data: Partial<Appointment>) => api.put<Appointment>(`/appointments/${id}`, data),
  delete: (id: string) => api.delete(`/appointments/${id}`),
  getByStatus: (status: string) => api.get<Appointment[]>(`/appointments/status/${status}`),
  getAvailableSlots: (date: string) => api.get<{ availableSlots: string[], bookedSlots: string[] }>(`/appointments/available-slots/${date}`),
  getByEmail: (email: string) => api.get<Appointment[]>(`/appointments/email/${email}`),
  lookup: (params: { email?: string; phone?: string }) =>
    api.get<Appointment[]>('/appointments/lookup', { params }),
  requestOtp: (email: string) =>
    api.post<{ message: string }>('/appointments/request-otp', { email }),
  verifyOtp: (email: string, code: string) =>
    api.post<{ ownershipToken: string; expiresIn: number; message: string }>('/appointments/verify-otp', { email, code }),
  getByManageToken: (token: string) =>
    api.get<Appointment>(`/appointments/manage/${encodeURIComponent(token)}`),
  selfUpdate: (
    id: string,
    data: Partial<AppointmentFormData>,
    auth: { ownershipToken?: string; manageToken?: string }
  ) =>
    api.put<Appointment>(`/appointments/self/${id}`, {
      ...data,
      ...(auth.manageToken ? { manageToken: auth.manageToken } : {}),
    }, {
      headers: auth.ownershipToken ? { Authorization: `Bearer ${auth.ownershipToken}` } : undefined,
    }),
  selfCancel: (
    id: string,
    auth: { ownershipToken?: string; manageToken?: string }
  ) =>
    api.post<{ message: string; appointment: Appointment }>(`/appointments/self/${id}/cancel`, {
      ...(auth.manageToken ? { manageToken: auth.manageToken } : {}),
    }, {
      headers: auth.ownershipToken ? { Authorization: `Bearer ${auth.ownershipToken}` } : undefined,
    }),
};

// Article API
export const articleAPI = {
  getAll: (params?: { page?: number; limit?: number; category?: string; search?: string }) =>
    api.get<{ articles: Article[]; pagination: { page: number; limit: number; total: number; pages: number } }>('/articles', { params }),
  getById: (id: string) => api.get<Article>(`/articles/${id}`),
  create: (data: Partial<Article>) => api.post<Article>('/articles', data),
  update: (id: string, data: Partial<Article>) => api.put<Article>(`/articles/${id}`, data),
  delete: (id: string) => api.delete(`/articles/${id}`),
  getByCategory: (category: string) => api.get<Article[]>(`/articles/category/${category}`),
  getAdminAll: () => api.get<Article[]>('/articles/admin/all'),
};

// Service API
export const serviceAPI = {
  getAll: () => api.get<{ services: Service[] }>('/services'),
  getById: (id: string) => api.get<Service>(`/services/${id}`),
  getAdminById: (id: string) => api.get<Service>(`/services/admin/${id}`),
  create: (data: Partial<Service>) => api.post<Service>('/services', data),
  update: (id: string, data: Partial<Service>) => api.put<Service>(`/services/${id}`, data),
  delete: (id: string) => api.delete(`/services/${id}`),
  getAdminAll: () => api.get<Service[]>('/services/admin/all'),
};

// Gallery API
export const galleryAPI = {
  getAll: (params?: { category?: string; featured?: boolean; limit?: number }) =>
    api.get<GalleryItem[]>('/gallery', { params }),
  getById: (id: string) => api.get<GalleryItem>(`/gallery/${id}`),
  create: (data: Partial<GalleryItem>) => api.post<GalleryItem>('/gallery', data),
  update: (id: string, data: Partial<GalleryItem>) => api.put<GalleryItem>(`/gallery/${id}`, data),
  delete: (id: string) => api.delete(`/gallery/${id}`),
  getByCategory: (category: string) => api.get<GalleryItem[]>(`/gallery/category/${category}`),
  getFeatured: () => api.get<GalleryItem[]>('/gallery/featured/all'),
  upload: (data: FormData) => api.post<GalleryItem>('/gallery/upload', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateFile: (id: string, data: FormData) => api.put<GalleryItem>(`/gallery/${id}/upload`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  addFiles: (id: string, data: FormData) => api.post<GalleryItem>(`/gallery/${id}/files`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteFile: (id: string, fileId: string) => api.delete<GalleryItem>(`/gallery/${id}/files/${fileId}`),
  updateFileMetadata: (id: string, fileId: string, data: { altText?: string; caption?: string }) =>
    api.put<GalleryItem>(`/gallery/${id}/files/${fileId}`, data),
};

export interface MediaUploadResponse {
  message: string;
  url: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
}

export interface MediaFolder {
  _id: string;
  name: string;
  slug: string;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaTag {
  _id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export interface MediaFileItem {
  filename: string;
  url: string;
  size: number;
  type: 'image' | 'video';
  uploadedAt: string;
  altText?: string;
  caption?: string;
  description?: string;
  folderId?: string | null;
  tags?: MediaTag[];
  width?: number;
  height?: number;
}

export interface MediaListResponse {
  files: MediaFileItem[];
}

export const mediaAPI = {
  getAll: (params?: { q?: string; folderId?: string; tagId?: string }) =>
    api.get<MediaListResponse>('/media/files', { params }),
  upload: (data: FormData) =>
    api.post<MediaUploadResponse>('/media/upload', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  updateMetadata: (filename: string, data: { altText?: string; caption?: string; description?: string }) =>
    api.put<{ message: string; media: any }>(`/media/files/${encodeURIComponent(filename)}`, data),
  moveFile: (filename: string, folderId: string | null) =>
    api.put<{ message: string; media: any }>(`/media/files/${encodeURIComponent(filename)}/move`, { folderId }),
  tagFile: (filename: string, tagIds: string[]) =>
    api.put<{ message: string; media: any }>(`/media/files/${encodeURIComponent(filename)}/tags`, { tagIds }),
  delete: (filename: string) =>
    api.delete<{ message: string; filename: string }>(`/media/files/${encodeURIComponent(filename)}`),
};

export const folderAPI = {
  getAll: () => api.get<MediaFolder[]>('/media/folders'),
  create: (data: { name: string; parentId?: string | null }) => api.post<MediaFolder>('/media/folders', data),
  rename: (id: string, name: string) => api.put<MediaFolder>(`/media/folders/${id}`, { name }),
  delete: (id: string) => api.delete<{ message: string; id: string }>(`/media/folders/${id}`),
};

export const tagAPI = {
  getAll: () => api.get<MediaTag[]>('/media/tags'),
  create: (name: string) => api.post<MediaTag>('/media/tags', { name }),
};

// Contact API
export const contactAPI = {
  getAll: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Contact>>('/contact', { params }),
  getById: (id: string) => api.get<Contact>(`/contact/${id}`),
  create: (data: ContactFormData) => api.post<Contact>('/contact', data),
  update: (id: string, data: Partial<Contact>) => api.put<Contact>(`/contact/${id}`, data),
  delete: (id: string) => api.delete(`/contact/${id}`),
  getByStatus: (status: string) => api.get<Contact[]>(`/contact/status/${status}`),
};

// About API
export const aboutAPI = {
  getAll: (params?: { section?: string }) => api.get<About[]>('/about', { params }),
  getById: (id: string) => api.get<About>(`/about/${id}`),
  create: (data: Partial<About>) => api.post<About>('/about', data),
  update: (id: string, data: Partial<About>) => api.put<About>(`/about/${id}`, data),
  delete: (id: string) => api.delete(`/about/${id}`),
  getBySection: (section: string) => api.get<About[]>(`/about/section/${section}`),
  getAdminAll: () => api.get<About[]>('/about/admin/all'),
};

// Comment API
export const commentAPI = {
  getAll: (params?: { articleId?: string; page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Comment>>('/comments', { params }),
  getById: (id: string) => api.get<Comment>(`/comments/${id}`),
  create: (data: CommentFormData) => api.post<Comment>('/comments', data),
  update: (id: string, data: Partial<Comment>) => api.put<Comment>(`/comments/${id}`, data),
  delete: (id: string) => api.delete(`/comments/${id}`),
  getByArticle: (articleId: string) => api.get<Comment[]>(`/comments/article/${articleId}`),
  getAdminAll: () => api.get<Comment[]>('/comments/admin/all'),
  getPending: () => api.get<Comment[]>('/comments/pending/all'),
};

// Google Reviews API
export interface GoogleReviewData {
  _id: string;
  placeId: string;
  authorName: string;
  rating: number;
  text: string;
  time: string;
  profilePhotoUrl: string;
  reviewId: string;
  cachedAt: string;
  createdAt: string;
}

export interface GoogleReviewsResponse {
  reviews: GoogleReviewData[];
  averageRating: number;
  totalReviews: number;
  googlePlaceId?: string;
  cached?: boolean;
  stale?: boolean;
  demo?: boolean;
}

export const reviewAPI = {
  getReviews: () => api.get<GoogleReviewsResponse>('/reviews'),
};

// Feedback API
export const feedbackAPI = {
  getAll: (params?: { serviceType?: string; page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Feedback>>('/feedback', { params }),
  getById: (id: string) => api.get<Feedback>(`/feedback/${id}`),
  create: (data: FeedbackFormData) => api.post<Feedback>('/feedback', data),
  update: (id: string, data: Partial<Feedback>) => api.put<Feedback>(`/feedback/${id}`, data),
  delete: (id: string) => api.delete(`/feedback/${id}`),
  getByService: (serviceType: string) => api.get<Feedback[]>(`/feedback/service/${serviceType}`),
  getAdminAll: () => api.get<Feedback[]>('/feedback/admin/all'),
  getPending: () => api.get<Feedback[]>('/feedback/pending/all'),
  getStats: () => api.get('/feedback/stats/all'),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (data: { email: string; name?: string; source?: string }) =>
    api.post<{ message: string }>('/newsletter/subscribe', data),
  unsubscribe: (email: string) =>
    api.post<{ message: string }>('/newsletter/unsubscribe', { email }),
  getSubscribers: () => api.get<any[]>('/newsletter/subscribers'),
};

// Page API
export const pageAPI = {
  getAll: () => api.get<StaticPage[]>('/pages'),
  getBySlug: (slug: string) => api.get<StaticPage>(`/pages/slug/${slug}`),
  getById: (id: string) => api.get<StaticPage>(`/pages/${id}`),
  create: (data: Partial<StaticPage>) => api.post<StaticPage>('/pages', data),
  update: (id: string, data: Partial<StaticPage>) => api.put<StaticPage>(`/pages/${id}`, data),
  delete: (id: string) => api.delete(`/pages/${id}`),
};

export interface SiteSettings {
  _id?: string;
  clinicName: string;
  phone: string;
  email: string;
  address: string;
  googleMapsLink?: string;
  facebookLink?: string;
  instagramLink?: string;
  youtubeLink?: string;
  linkedinLink?: string;
  appointmentNotificationEmail?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPass?: string;
}

export const settingsAPI = {
  get: () => api.get<SiteSettings>('/settings'),
  update: (data: Partial<SiteSettings>) => api.put<{ message: string; settings: SiteSettings }>('/settings', data),
};

export const seoAPI = {
  getAll: () => api.get<CustomSeo[]>('/seo'),
  getById: (id: string) => api.get<CustomSeo>(`/seo/${id}`),
  getByUrl: (url: string) => api.get<CustomSeo>('/seo/by-url', { params: { url } }),
  create: (data: Partial<CustomSeo>) => api.post<CustomSeo>('/seo', data),
  update: (id: string, data: Partial<CustomSeo>) => api.put<CustomSeo>(`/seo/${id}`, data),
  delete: (id: string) => api.delete(`/seo/${id}`),
};

export default api;
